import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { resendVerificationEmail } from "@/lib/auth";
import { AuthShell, Field, GoldButton, authInputClass } from "@/components/auth/AuthShell";

export const Route = createFileRoute("/verify-email")({
  head: () => ({
    meta: [
      { title: "Verify Your Email — CB16" },
      { name: "description", content: "Confirm the email address for your CB16 account." },
      { property: "og:title", content: "Verify Your Email — CB16" },
      { property: "og:description", content: "Confirm the email address for your CB16 account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VerifyEmailPage,
});

type State = "checking" | "verified" | "expired" | "invalid" | "idle";

/** Read the error the auth provider appends to the link (hash or query), if any. */
function linkError(): { code: string; description: string } | null {
  if (typeof window === "undefined") return null;
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);
  const code = hash.get("error_code") || query.get("error_code") || hash.get("error") || query.get("error");
  if (!code) return null;
  return {
    code,
    description: hash.get("error_description") || query.get("error_description") || "",
  };
}

function VerifyEmailPage() {
  const [state, setState] = useState<State>("checking");

  useEffect(() => {
    const err = linkError();
    if (err) {
      // Remove the error details from the address bar once read.
      window.history.replaceState(null, "", window.location.pathname);
      const expired = /expired|otp_expired/i.test(`${err.code} ${err.description}`);
      setState(expired ? "expired" : "invalid");
      return;
    }
    let done = false;
    const finish = (verified: boolean) => {
      if (done) return;
      done = true;
      setState(verified ? "verified" : "idle");
    };
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email_confirmed_at) finish(true);
    });
    void supabase.auth.getSession().then(({ data: s }) => {
      if (s.session?.user?.email_confirmed_at) finish(true);
    });
    // The link is exchanged for a session automatically; give it a moment.
    const t = setTimeout(() => finish(false), 2500);
    return () => {
      clearTimeout(t);
      data.subscription.unsubscribe();
    };
  }, []);

  if (state === "checking") {
    return (
      <AuthShell title="Verifying your email" subtitle="One moment…">
        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-black/10 border-t-[#C6A15B]" />
      </AuthShell>
    );
  }

  if (state === "verified") {
    return (
      <AuthShell title="Email verified" subtitle="Your CB16 account is ready">
        <div className="space-y-5" data-testid="verify-email-success">
          <p className="text-center text-sm text-black/70">
            Thank you — your email address has been confirmed. You can now sign in and shop.
          </p>
          <Link
            to="/account"
            className="block w-full bg-[#C6A15B] py-3 text-center text-sm font-medium uppercase tracking-[0.2em] text-white transition hover:bg-[#b18f4d]"
          >
            Continue to My Account
          </Link>
          <Link to="/" className="block text-center text-xs tracking-wide text-black/60 hover:text-[#C6A15B]">
            Return to the homepage
          </Link>
        </div>
      </AuthShell>
    );
  }

  const title =
    state === "expired"
      ? "This verification link has expired"
      : state === "invalid"
        ? "This verification link is not valid"
        : "Verify your email";
  const subtitle =
    state === "idle"
      ? "Open the verification link from your email, or request a new one below."
      : "Enter your email and we'll send you a new verification link.";
  return (
    <AuthShell title={title} subtitle={subtitle}>
      <ResendForm />
    </AuthShell>
  );
}

function ResendForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = z.string().trim().email().safeParse(email);
    if (!parsed.success) {
      setError("Enter a valid email address.");
      return;
    }
    setError(null);
    setLoading(true);
    const res = await resendVerificationEmail(parsed.data);
    setLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setSentTo(parsed.data);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="verify-email-resend">
      {sentTo && (
        <p role="status" className="border border-[#C6A15B]/40 bg-[#C6A15B]/10 px-4 py-3 text-center text-xs text-black/80">
          If {sentTo} has an account awaiting verification, a new link is on its way. Check your
          inbox and Spam/Junk folder.
        </p>
      )}
      <Field label="Email" htmlFor="verify-email" error={error ?? undefined}>
        <input
          id="verify-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={authInputClass}
        />
      </Field>
      <GoldButton type="submit" disabled={loading}>
        {loading ? "Sending…" : "Resend Verification Email"}
      </GoldButton>
      <Link
        to="/login"
        className="block w-full border border-black py-3 text-center text-sm font-medium uppercase tracking-[0.2em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
      >
        Return to Sign In
      </Link>
    </form>
  );
}
